import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react'
import { useSelector } from 'react-redux'
import queryString from 'query-string'
import StatelessDataTable from '../../../app/components/statelessDataTable'
import createColumns from './components/columns/columns'
import { downloadFile, filterLocales, stateList } from '../../../common/helpers'
import DefaultErrorMessage from '../../../common/components/ErrorBoundary/errorMessage'
import LEGACY_PRODUCTS from '../../../common/enum/legacyProducts'
import { CSVDownloadError } from '../../../common/components/CSVDownloadError/CSVDownloadError'
import { getPostingsLocations, generateUpdateServiceCSV, generateSubscriptionAuditCSV } from './services/postingsLocationsService'

const PostingsLocations = ({ location }) => {
  const companyId = useSelector((state) => state.account.company._id)

  const [loading, setLoading] = useState(Boolean(companyId))
  const [error, setError] = useState(false)
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortOptions, setSortOptions] = useState({
    field: 'displayName',
    direction: 'asc'
  })
  const [filterOptions, setFilterOptions] = useState({
    filter: '',
    field: ''
  })

  const handleClose = () => setErrorPopupOpen(false)

  // Hacky way to keep track of the current search params - see ELC-1287 for more info
  const searchParams = useRef({ pageNumber, searchText })

  const queryParams = queryString.parse(location.search)
  let initialFilters = { state: '' }
  if (queryParams && queryParams.state) {
    initialFilters = {
      state: {
        locale: {
          state: queryParams.state,
          name: stateList.find((state) => state.state === queryParams.state).name
        }
      }
    }
  }
  const [filters, setFilters] = useState(initialFilters)

  const [columns, setColumns] = useState(createColumns({
    sortOptions,
    filterOptions,
    eventHandlers: {
      setSortOptions,
      setFilterOptions,
      setPageNumber
    }
  }))
  const [locales, setLocales] = useState([])

  const filterLocalesList = (facets) => {
    const filteredLocales = filterLocales(LEGACY_PRODUCTS.POSTINGS, facets)
    setLocales(filteredLocales)
  }

  const pageSize = 20

  useEffect(() => {
    searchParams.current = { pageNumber, searchText }
  }, [pageNumber, searchText])

  const fetchPostingsLocations = async () => {
    let subscribed = true
    setLoading(true)
    setData([])

    try {
      const locationsResponse = await getPostingsLocations({
        searchText,
        sortOptions,
        filterOptions,
        pageNumber,
        pageSize,
        companyId,
        filters
      })

      if (subscribed && searchParams.current.searchText === searchText && searchParams.current.pageNumber === pageNumber) {
        setData(locationsResponse.locations)
        setTotal(locationsResponse.total)
        filterLocalesList(locationsResponse.localeFacets)
        setColumns(createColumns({
          refreshPostingsLocations,
          sortOptions,
          filterOptions,
          eventHandlers: {
            setSortOptions,
            setFilterOptions,
            setPageNumber
          }
        }))
        setLoading(false)
      }

      return () => (subscribed = false)
    } catch (queryLocationsError) {
      setLoading(false)
      console.error('error', queryLocationsError || 'unknown error')
      setError(queryLocationsError)
    }

    return false
  }

  const refreshPostingsLocations = () => {
    fetchPostingsLocations()
  }

  useEffect(() => {
    fetchPostingsLocations()
  }, [pageNumber, filters, searchText, sortOptions, filterOptions, companyId])

  const handleChangePage = useCallback((event) => {
    setPageNumber(event.selected + 1)
    setData([])
    setLoading(true)
  }, [])

  const handleSetSearchText = useCallback((value) => {
    setSearchText(value)
  }, [])

  const handleSetSortOptions = useCallback((value) => {
    setSortOptions(value)
  }, [])

  const handleSetFilters = useCallback((value) => {
    setFilters(value)
  }, [])

  const handleSetPageNumber = useCallback((value) => {
    setPageNumber(value)
  }, [])

  const handleUpdateServiceExportCSV = async () => {
    setLoading(true)
    try {
      const response = await generateUpdateServiceCSV({
        searchText,
        sortOptions,
        filterOptions,
        companyId,
        filters
      })
      if (response && response.downloadUrl) {
        downloadFile(response.downloadUrl)
      } else {
        setErrorPopupOpen(true)
        console.error('Update Service CSV generation error.')
      }
    } catch (exportCsvError) {
      setErrorPopupOpen(true)
      console.error('Error while fetching update service csv file ', exportCsvError)
    }
    setLoading(false)
  }

  const handleSubscriptionAuditExportCSV = async () => {
    setLoading(true)
    try {
      const response = await generateSubscriptionAuditCSV({
        searchText,
        sortOptions,
        filterOptions,
        companyId,
        filters
      })
      if (response && response.downloadUrl) {
        downloadFile(response.downloadUrl)
      } else {
        setErrorPopupOpen(true)
        console.error('Subscription Audit CSV generation error.')
      }
    } catch (exportCsvError) {
      setErrorPopupOpen(true)
      console.error('Error while fetching subscription audit csv file ', exportCsvError)
    }
    setLoading(false)
  }

  return (error)
    ? <DefaultErrorMessage error={error} data-testid="error-container" />
    : (
      <Fragment>
        <StatelessDataTable
          pageCount={Math.ceil(total / pageSize)}
          isLoading={loading}
          recordType="Locations"
          showSearch
          showExportDropdown
          exportOptions={['Update Service', 'Subscription Audit']}
          showLocaleFilter
          eventHandlers={{
            handleChangePage,
            setSearchText: handleSetSearchText,
            setSortOptions: handleSetSortOptions,
            setFilters: handleSetFilters,
            exportUpdateService: handleUpdateServiceExportCSV,
            exportItems: handleSubscriptionAuditExportCSV,
            setPageNumber: handleSetPageNumber
          }}
          columns={columns}
          data={data}
          filters={filters}
          sortOptions={sortOptions}
          filterOptions={filterOptions}
          pageNumber={pageNumber}
          total={total}
          searchText={searchText}
          rowsPerPage={pageSize}
          locales={locales}
        />
        <CSVDownloadError open={errorPopupOpen} onClose={handleClose} />
      </Fragment>
    )
}

export default PostingsLocations
